import { Alert, Button, textareaClass } from '@ledgerloom/ui';
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useApi, usePoll } from '../hooks';
import { ErrorBanner, Icon } from './ui';

interface Conversation {
  agent: { id: string; name: string; status: string } | null;
  thread: { id: string; status: string; answering: boolean; messages: Array<{ n: number; role: 'asker' | 'owner' | 'person'; body: string; new_topic: boolean }> } | null;
}
type Thread = NonNullable<Conversation['thread']>;
export type ChatState = 'idle' | 'listen' | 'think' | 'done' | 'error';

// The screen line the help route adds to a question ("Asked on the Deals screen (/deals/:id)."), shown apart from it.
const SCREEN_LINE = /\n\nAsked on the (.+?) screen(?: \([^)]*\))?\.$/;

// A person's chat with an agent that answers as them (ai-intelligence/ai-chat):
// the Inbox agent about one item, the help agent about Mazu. base serves
// GET {base}/conversation and POST {base}/ask. starters are questions to send
// with one press before the first one; body goes with each question;
// onAnswered runs when a reply lands, for a page that shows what it drafted.
// query goes on the conversation's address (which agent or thread), and extra sits under the heading.
// fresh shows no thread until the first question; onAsked hears the thread a question went to.
// onNewTopic offers a new conversation when the agent says a question was a new topic; onEnded,
// for a thread opened by id, offers one when that thread is over; after renders below the chat.
// follow, in a panel that scrolls, opens at the newest message and keeps each new one in view.
// onState hears what the chat is doing, for the Mazu head: listen while a question is typed, think
// while it is asked and answered, done for a moment when an answer lands, error when asking failed.
export function AskAgent({ base, title, level = 3, fallbackName, hint, working, starters = [], body, query = '', extra, className, onAnswered, fresh, onAsked, onNewTopic, onEnded, after, follow, onState }: {
  base: string; title: string; level?: 2 | 3; fallbackName: string; hint: string; working: string; starters?: string[]; body?: Record<string, unknown>; query?: string; extra?: ReactNode; className?: string; onAnswered?: () => unknown;
  fresh?: boolean; onAsked?: (threadId: string) => void; onNewTopic?: () => void; onEnded?: () => void; after?: (thread: Thread) => ReactNode; follow?: boolean;
  onState?: (state: ChatState) => void;
}) {
  const id = useId();
  const section = useRef<HTMLElement>(null);
  const hintId = useId();
  const c = useApi<Conversation>(`${base}/conversation${query}`, [base, query]);
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const agent = c.data?.agent ?? null;
  const thread = fresh ? null : c.data?.thread ?? null;
  const answering = !!thread?.answering;
  usePoll(answering, () => void c.reload());
  const wasAnswering = useRef(false);
  const [landed, setLanded] = useState(false);
  useEffect(() => {
    if (wasAnswering.current && !answering) { void onAnswered?.(); setLanded(true); }
    wasAnswering.current = answering;
  }, [answering]);
  useEffect(() => { if (!landed) return; const t = setTimeout(() => setLanded(false), 2500); return () => clearTimeout(t); }, [landed]);
  const state: ChatState = error || c.error ? 'error' : busy || answering ? 'think' : question.trim() ? 'listen' : landed ? 'done' : 'idle';
  useEffect(() => { onState?.(state); }, [state]); // eslint-disable-line react-hooks/exhaustive-deps
  // Only a new message or the answer starting or landing moves it, so a reload never pulls a reader back down.
  const count = thread?.messages.length ?? 0;
  useEffect(() => { if (follow && count) section.current?.scrollIntoView({ block: 'end' }); }, [follow, count, answering]);
  const name = agent?.name ?? fallbackName;
  // The last turn is the person's, nothing is working on it, and the thread is over or idle: no answer came.
  const unanswered = !!thread && !answering && thread.messages.at(-1)?.role === 'person';
  const Heading = level === 2 ? 'h2' : 'h3';

  const canSend = (text: string) => !busy && !answering && text.trim().length >= 5;
  const send = async (text: string) => {
    setBusy(true); setError(null);
    try {
      const out = await api<{ thread_id: string }>(`${base}/ask`, { body: { ...body, question: text.trim() } });
      setQuestion('');
      await c.reload();
      onAsked?.(out.thread_id);
    } catch (err) { setError(err); } finally { setBusy(false); }
  };
  const ask = (e: FormEvent) => { e.preventDefault(); void send(question); };
  return (
    <section ref={section} className={`stack ${className ?? ''}`} style={{ gap: 8 }} aria-labelledby={id}>
      <Heading id={id}>{title}</Heading>
      {extra}
      <ErrorBanner error={c.error} />
      {c.data && !agent && <p className="faint" style={{ margin: 0 }}>This workspace has no {fallbackName}.</p>}
      {agent && agent.status !== 'active' && (
        <Alert role="note" tone="info">{name} is not turned on yet. An admin turns it on from <Link to={`/agents/${encodeURIComponent(agent.id)}`}>its page</Link>.</Alert>
      )}
      {thread && thread.messages.length > 0 && (
        <ol className="chat" aria-label={`Your questions and ${name}'s answers`}>
          {thread.messages.map((m) => {
            const screen = m.body.match(SCREEN_LINE);
            return (
              <li key={m.n} className={m.role === 'owner' ? 'from-agent' : 'from-you'}>
                <div className="faint small">{m.role === 'owner' ? name : 'You'}{screen && ` · on ${screen[1]}`}</div>
                <div className="chat-bubble">{screen ? m.body.slice(0, screen.index) : m.body}</div>
              </li>
            );
          })}
        </ol>
      )}
      {onNewTopic && !answering && thread?.messages.at(-1)?.new_topic && (
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <span className="faint small">That looks like a new topic.</span>
          <Button type="button" variant="secondary" size="sm" onClick={onNewTopic}><Icon name="plus" />Start a new conversation</Button>
        </div>
      )}
      {answering && <div role="status" className="faint">{name} {working}…</div>}
      {unanswered && <div className="faint">No answer came. Ask again.</div>}
      {thread && after?.(thread)}
      {onEnded && thread && thread.status !== 'open' && (
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <span className="faint small">This conversation has ended.</span>
          <Button type="button" variant="secondary" size="sm" onClick={onEnded}><Icon name="plus" />Start a new one</Button>
        </div>
      )}
      {agent?.status === 'active' && !(onEnded && thread && thread.status !== 'open') && (
        <form onSubmit={ask} className="stack chat-form" style={{ gap: 6 }}>
          {!thread?.messages.length && !question && starters.length > 0 && (
            <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
              {starters.map((s) => <Button key={s} type="button" variant="secondary" size="sm" disabled={!canSend(s)} onClick={() => void send(s)}>{s}</Button>)}
            </div>
          )}
          <div className="chat-composer">
            <textarea className={`${textareaClass} chat-input`} rows={1} maxLength={4000} value={question} onChange={(e) => setQuestion(e.target.value)}
              aria-label={thread?.status === 'open' && thread.messages.length > 1 ? 'Ask a follow-up' : 'Your question'} aria-describedby={hintId}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
                e.preventDefault();
                if (canSend(question)) void send(question);
              }} />
            <Button type="submit" variant="secondary" disabled={!canSend(question)}><Icon name="agent" />Ask</Button>
          </div>
          <div id={hintId} className="faint small">{hint.replace('{name}', name)} Enter sends, Shift+Enter starts a new line.</div>
          <ErrorBanner error={error} />
        </form>
      )}
    </section>
  );
}
